import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Wallet, ShieldCheck, AlertTriangle, RotateCcw, Ban, User as UserIcon,
  Minus, TrendingUp, X, DollarSign, Clock, Car, Gauge, Fuel, FileText, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { Skeleton, F } from "./_shared";

const STATUS_META = {
  held:      { label: "Held",       color: "bg-amber-400/15  text-amber-300 border-amber-400/40" },
  applied:   { label: "Applied → Revenue", color: "bg-lime-400/15  text-lime-300  border-lime-400/40" },
  refunded:  { label: "Refunded",   color: "bg-white/8       text-white/70  border-white/20" },
  forfeited: { label: "Forfeited",  color: "bg-red-500/15    text-red-300   border-red-500/40" },
  refund_pending:         { label: "Refund pending",         color: "bg-cyan/15    text-cyan       border-cyan/40" },
  partial_refund_pending: { label: "Partial refund pending", color: "bg-cyan/15    text-cyan       border-cyan/40" },
  pending:   { label: "Pending",    color: "bg-white/8       text-white/60  border-white/20" },
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function DepositsTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("held");
  const [typeFilter, setTypeFilter] = useState(""); // "" | "ride" | "rental"
  const [search, setSearch] = useState("");
  const [action, setAction] = useState(null); // {type, row}

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (typeFilter)   params.set("booking_type", typeFilter);
    const qs = params.toString();
    Promise.all([
      api.get("/admin/deposits" + (qs ? `?${qs}` : "")),
      api.get("/admin/deposits/summary"),
    ])
      .then(([a, b]) => { setRows(a.data || []); setSummary(b.data); })
      .catch(() => toast.error("Failed to load deposits"))
      .finally(() => setLoading(false));
  }, [statusFilter, typeFilter]);

  useEffect(load, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(q) ||
      (r.customer_phone || "").toLowerCase().includes(q) ||
      (r.customer_email || "").toLowerCase().includes(q) ||
      (r.vehicle_name || "").toLowerCase().includes(q) ||
      (r.booking_id || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div data-testid="deposits-tab" className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Currently held" tint="#FFB020" icon={ShieldCheck}
          value={fmt(summary?.held)} testid="dep-kpi-held"
          sub={summary?.held_count ? `${summary.held_count} customers` : "—"} />
        <KpiCard label="Revenue realized" tint="#39D6FF" icon={TrendingUp}
          value={fmt(summary?.revenue_realized)} testid="dep-kpi-realized"
          sub="applied + deductions + balances" />
        <KpiCard label="Deductions collected" tint="#A3E635" icon={Minus}
          value={fmt(summary?.deductions_collected)} testid="dep-kpi-deducted"
          sub="damage / cleaning / late" />
        <KpiCard label="Refunded" tint="#8B5CF6" icon={RotateCcw}
          value={fmt(summary?.refunded)} testid="dep-kpi-refunded"
          sub="returned to customers" />
        <KpiCard label="Forfeited" tint="#FF3EA5" icon={Ban}
          value={fmt(summary?.forfeited)} testid="dep-kpi-forfeited"
          sub="no-show / policy" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="glass rounded-full p-1 flex flex-wrap" data-testid="deposits-status-filter">
          {[
            { k: "held", l: "Held" },
            { k: "applied", l: "Applied" },
            { k: "refund_pending", l: "Refund pending" },
            { k: "partial_refund_pending", l: "Partial refund" },
            { k: "refunded", l: "Refunded" },
            { k: "forfeited", l: "Forfeited" },
            { k: "", l: "All" },
          ].map((t) => (
            <button key={t.k} type="button"
              data-testid={`deposits-filter-${t.k || "all"}`}
              onClick={() => setStatusFilter(t.k)}
              className={`px-3 py-1.5 rounded-full text-[11px] label-caps transition ${
                statusFilter === t.k
                  ? "bg-cyan text-midnight"
                  : "text-white/60 hover:text-white"
              }`}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="glass rounded-full p-1 flex" data-testid="deposits-type-filter">
          {[
            { k: "", l: "All types" },
            { k: "ride", l: "Rides" },
            { k: "rental", l: "Rentals" },
          ].map((t) => (
            <button key={t.k || "all"} type="button"
              data-testid={`deposits-type-${t.k || "all"}`}
              onClick={() => setTypeFilter(t.k)}
              className={`px-3 py-1.5 rounded-full text-[11px] label-caps transition ${
                typeFilter === t.k
                  ? "bg-cyan text-midnight"
                  : "text-white/60 hover:text-white"
              }`}>
              {t.l}
            </button>
          ))}
        </div>
        <input
          data-testid="deposits-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by customer name, phone, email, vehicle, booking ID…"
          className="flex-1 min-w-[240px] bg-ink-800 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-cyan"
        />
      </div>

      {/* Rows */}
      {loading ? <Skeleton /> : filtered.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center text-white/60">
          No deposits match this filter yet.
        </div>
      ) : (
        <div data-testid="deposits-list" className="space-y-2">
          {filtered.map((r) => (
            <DepositRow key={r.booking_id} row={r} onAction={(type) => setAction({ type, row: r })} />
          ))}
        </div>
      )}

      {action && (
        <DepositActionModal
          action={action}
          onClose={() => setAction(null)}
          onDone={() => { setAction(null); load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tint, testid }) {
  return (
    <div data-testid={testid}
      className="glass rounded-2xl p-4 border border-white/5 relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl"
        style={{ background: tint }} />
      <div className="flex items-center justify-between mb-1.5">
        <div className="label-caps text-white/50 text-[10px]">{label}</div>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: `${tint}22`, color: tint }}>
          <Icon size={12} />
        </div>
      </div>
      <div className="font-serif text-2xl">{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function DepositRow({ row, onAction }) {
  const st = STATUS_META[row.deposit_status] || STATUS_META.pending;
  const isHeld = row.deposit_status === "held";
  const hasDeductions = (row.deductions || []).length > 0;
  const isRental = row.type === "rental";
  const rental = row.rental || null;
  const hasReturn = !!(rental && rental.return_completed_at);
  const downloadReturnPdf = async () => {
    try {
      const url = `${BACKEND}/api/admin/rentals/${row.booking_id}/return-summary-pdf`;
      const token = localStorage.getItem("pr_session_token") || "";
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `plenty-rydez-${row.booking_id}-return-summary.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    } catch (e) {
      toast.error("Return summary PDF unavailable yet");
    }
  };
  return (
    <div data-testid={`deposit-row-${row.booking_id}`}
      className="glass rounded-2xl p-4 border border-white/5 hover:border-cyan/30 transition">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-2.5 min-w-[220px]">
          <div className="w-10 h-10 rounded-full bg-cyan/15 text-cyan flex items-center justify-center">
            <UserIcon size={16} />
          </div>
          <div>
            <div className="text-white font-medium leading-tight flex items-center gap-1.5">
              {row.customer_name}
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] label-caps border ${
                isRental
                  ? "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40"
                  : "bg-cyan/15 text-cyan border-cyan/40"
              }`} data-testid={`deposit-type-badge-${row.booking_id}`}>
                {isRental ? <Car size={9} /> : <UserIcon size={9} />}
                {isRental ? "Rental" : "Ride"}
              </span>
            </div>
            <div className="text-[11px] text-white/50 leading-tight">
              {row.customer_phone || row.customer_email || "—"}
            </div>
          </div>
        </div>
        <div className="text-xs text-white/70 min-w-[180px]">
          <div>{row.vehicle_name}</div>
          <div className="text-[10px] text-white/40">
            {row.trip_date ? new Date(row.trip_date).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
            }) : "—"}
          </div>
          <div className="text-[10px] text-white/30 font-mono mt-0.5">{row.booking_id}</div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-serif text-white">{fmt(row.remaining)}</span>
            <span className="text-[10px] label-caps text-white/40">remaining</span>
          </div>
          <div className="text-[11px] text-white/50 mt-0.5">
            Paid <span className="text-white/80">{fmt(row.deposit_paid_amount)}</span>
            {row.deducted_total > 0 && (
              <> · Deducted <span className="text-lime-300">{fmt(row.deducted_total)}</span></>
            )}
            {row.deposit_status === "refunded" && row.deposit_refund_amount != null && (
              <> · Refunded <span className="text-cyan">{fmt(row.deposit_refund_amount)}</span></>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1 rounded-full text-[10px] label-caps border ${st.color}`}>
            {st.label}
          </span>
        </div>
      </div>

      {/* Rental return breakdown (auto-computed at return handoff) */}
      {isRental && rental && hasReturn && (
        <div data-testid={`rental-return-breakdown-${row.booking_id}`}
          className="mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center justify-between mb-2">
            <div className="label-caps text-[10px] text-cyan flex items-center gap-1.5">
              <Car size={11} /> Return handoff ledger
            </div>
            <button type="button"
              data-testid={`dep-btn-return-pdf-${row.booking_id}`}
              onClick={downloadReturnPdf}
              className="text-[10px] label-caps rounded-full px-2.5 py-1 bg-cyan/15 text-cyan border border-cyan/40 hover:bg-cyan hover:text-midnight inline-flex items-center gap-1">
              <FileText size={10} /> Return summary PDF
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <MiniStat icon={Gauge} label="Miles driven"
              value={`${Number(rental.miles_driven || 0).toLocaleString()} mi`}
              sub={rental.overage_miles > 0 ? `+${rental.overage_miles} over cap` : "within limit"} />
            <MiniStat icon={Fuel} label="Fuel"
              value={`${rental.pickup_fuel_level || "—"} → ${rental.return_fuel_level || "—"}`}
              sub={rental.gallons_short > 0 ? `${rental.gallons_short} gal short` : "topped off"} />
            <MiniStat icon={AlertTriangle} label="Extra charges"
              value={fmt(rental.additional_charges)}
              sub={
                (rental.overage_charge > 0 || rental.fuel_charge > 0)
                  ? [
                      rental.overage_charge > 0 ? `${fmt(rental.overage_charge)} mileage` : null,
                      rental.fuel_charge > 0 ? `${fmt(rental.fuel_charge)} fuel` : null,
                    ].filter(Boolean).join(" · ")
                  : "no extras"
              } />
            <MiniStat icon={RotateCcw} label="Refund due"
              value={fmt(rental.deposit_refund_pending)}
              sub={rental.net_customer_owes > 0
                ? `${fmt(rental.net_customer_owes)} owed by customer`
                : (rental.deposit_withheld > 0 ? `${fmt(rental.deposit_withheld)} withheld` : "clean return")} />
          </div>
        </div>
      )}

      {hasDeductions && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="label-caps text-[10px] text-white/40 mb-1.5">Deductions</div>
          <div className="space-y-1">
            {row.deductions.map((d) => (
              <div key={d.id} className="flex items-center gap-3 text-[11px] text-white/70">
                <span className="text-lime-300 font-medium min-w-[60px]">{fmt(d.amount)}</span>
                <span className="text-white/50 label-caps text-[9px] min-w-[70px]">{d.category}</span>
                <span className="flex-1 truncate italic text-white/60">"{d.reason}"</span>
                <span className="text-white/30 text-[9px]">
                  {new Date(d.at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isHeld && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/5">
          <button type="button"
            data-testid={`dep-btn-deduct-${row.booking_id}`}
            onClick={() => onAction("deduct")}
            className="text-[11px] label-caps rounded-full px-3 py-1.5 bg-lime-400/15 text-lime-300 border border-lime-400/40 hover:bg-lime-400 hover:text-ink-900 inline-flex items-center gap-1">
            <Minus size={11} /> Deduct for damages
          </button>
          <button type="button"
            data-testid={`dep-btn-release-${row.booking_id}`}
            onClick={() => onAction("release")}
            className="text-[11px] label-caps rounded-full px-3 py-1.5 bg-cyan/15 text-cyan border border-cyan/40 hover:bg-cyan hover:text-midnight inline-flex items-center gap-1">
            <TrendingUp size={11} /> Release to revenue
          </button>
          <button type="button"
            data-testid={`dep-btn-refund-${row.booking_id}`}
            onClick={() => onAction("refund")}
            className="text-[11px] label-caps rounded-full px-3 py-1.5 bg-white/8 text-white/70 border border-white/20 hover:bg-white/15 hover:text-white inline-flex items-center gap-1">
            <RotateCcw size={11} /> Refund customer
          </button>
          <button type="button"
            data-testid={`dep-btn-forfeit-${row.booking_id}`}
            onClick={() => onAction("forfeit")}
            className="text-[11px] label-caps rounded-full px-3 py-1.5 bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500 hover:text-white inline-flex items-center gap-1">
            <Ban size={11} /> Forfeit
          </button>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-ink-950/50 rounded-xl px-3 py-2 border border-white/5">
      <div className="flex items-center gap-1.5 text-[9px] label-caps text-white/40 mb-0.5">
        <Icon size={10} />{label}
      </div>
      <div className="text-white text-sm font-medium leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}

function DepositActionModal({ action, onClose, onDone }) {
  const { type, row } = action;
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(row.remaining || 0);
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("damage");

  const titles = {
    deduct:  "Deduct from deposit",
    release: "Release to revenue",
    refund:  "Refund to customer",
    forfeit: "Forfeit deposit",
  };
  const submits = {
    deduct:  "Deduct",
    release: "Release",
    refund:  "Refund",
    forfeit: "Forfeit",
  };

  const submit = async (e) => {
    e.preventDefault();
    if ((type === "deduct" || type === "refund") && !reason.trim() && type === "deduct") {
      return toast.error("Please add a reason (audit trail)");
    }
    if (type === "deduct" && (amount <= 0 || amount > row.remaining + 0.01)) {
      return toast.error(`Amount must be between $0.01 and $${row.remaining.toFixed(2)}`);
    }
    setBusy(true);
    try {
      if (type === "deduct") {
        await api.post(`/admin/bookings/${row.booking_id}/deposit/deduct`,
          { amount: Number(amount), reason: reason.trim(), category });
      } else if (type === "release") {
        await api.post(`/admin/bookings/${row.booking_id}/deposit/release`, {});
      } else if (type === "refund") {
        await api.post(`/admin/bookings/${row.booking_id}/deposit/refund`,
          { reason: reason.trim(), refund_amount: Number(amount) });
      } else if (type === "forfeit") {
        await api.post(`/admin/bookings/${row.booking_id}/deposit/forfeit`,
          { reason: reason.trim() });
      }
      toast.success(`${titles[type]} recorded`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="deposit-action-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-ink-900 border border-cyan/40 rounded-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-caps text-cyan text-xs">{titles[type]}</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="bg-ink-950/60 rounded-xl p-3 text-xs space-y-0.5">
          <div className="text-white font-medium">{row.customer_name}</div>
          <div className="text-white/50">{row.vehicle_name}</div>
          <div className="text-white/50 flex justify-between mt-1">
            <span>Held: {fmt(row.deposit_paid_amount)}</span>
            {row.deducted_total > 0 && <span className="text-lime-300">Deducted: {fmt(row.deducted_total)}</span>}
            <span className="text-cyan font-medium">Remaining: {fmt(row.remaining)}</span>
          </div>
        </div>

        {(type === "deduct" || type === "refund") && (
          <F label={type === "deduct" ? "Deduction amount" : "Refund amount"}>
            <input type="number" step="0.01" min={0.01} max={row.remaining}
              data-testid="dep-modal-amount"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="nv-input" required />
          </F>
        )}

        {type === "deduct" && (
          <F label="Category">
            <select data-testid="dep-modal-category"
              value={category} onChange={(e) => setCategory(e.target.value)} className="nv-input">
              <option value="damage">Damage</option>
              <option value="cleaning">Cleaning</option>
              <option value="late_return">Late return</option>
              <option value="smoking">Smoking</option>
              <option value="mileage_overage">Mileage overage</option>
              <option value="other">Other</option>
            </select>
          </F>
        )}

        <F label={type === "deduct" ? "Reason (required for audit)" : "Reason / notes"}>
          <textarea rows={2}
            data-testid="dep-modal-reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="nv-input"
            placeholder={type === "deduct" ? "e.g. Scratch on rear bumper — photos attached" : "Optional notes"}
            required={type === "deduct"} />
        </F>

        {type === "refund" && (
          <div className="text-[11px] text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3">
            ⚠️ This records the accounting event only. You still need to process the actual refund in your Stripe/PayPal dashboard.
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose}
            className="flex-1 rounded-full py-2.5 text-xs label-caps border border-white/15 text-white/70 hover:text-white">
            Cancel
          </button>
          <button type="submit" data-testid="dep-modal-submit" disabled={busy}
            className="flex-[2] rounded-full py-2.5 text-xs label-caps bg-cyan text-midnight hover:brightness-110 disabled:opacity-50">
            {busy ? "Working…" : submits[type]}
          </button>
        </div>
      </form>
    </div>
  );
}
